import * as React from 'react';
import dayjs from 'dayjs';
import { useLocation, RouteComponentProps, Link, Redirect } from 'react-router-dom';
import { Card, Flex, Divider } from 'antd';
import { Player } from '@lottiefiles/react-lottie-player';

import Text from 'admin/components/common/Text';
import Button from 'admin/components/common/Button';
import { Icon } from 'admin/components/common/Icon';
import LoginLayout from 'admin/components/common/LoginLayout';
import { Grid } from 'admin/components/common/Grid';
import { ROOT } from 'admin/constants/routes';
import { PageTitle } from 'admin/components/common/PageLayout/PageTitle';
import { useGetUploadedFile, useGetUploadedFilePresignedUrl } from 'admin/queries/story/use-uploaded-file';
import SecureValidation from 'admin/assets/lottie/secure-validation.json';

import { formatFileSize, formatFileType, getFileType, getFileName, text, SCAN_STATUS_CONTENT } from './helpers';

import css from './UploadedFile.scss';

type Props = RouteComponentProps;

type FileDetail = {
	title: string;
	value: string;
};

type FileData = {
	fileName: string;
	fileType: string;
	fileDetails: FileDetail[];
	isDeleted: boolean;
	scanStatus: string;
};

const UploadedFile: React.FC<Props> = () => {
	const location = useLocation();
	const fileKey = new URLSearchParams(location.search).get('key') ?? '';

	const { data, isFetching } = useGetUploadedFile(encodeURIComponent(fileKey), { enabled: Boolean(fileKey) });
	const { refetch: getPresignedUrl, isFetching: isPending } = useGetUploadedFilePresignedUrl(
		encodeURIComponent(fileKey)
	);

	const fileData = React.useMemo<FileData>(() => {
		if (!data?.storyUpload)
			return {
				fileName: '',
				fileType: '',
				fileDetails: [],
				isDeleted: false,
				scanStatus: '',
			};

		const { storyUpload } = data;
		const fileName = getFileName(storyUpload.key);
		const type = getFileType(fileName);

		return {
			fileName,
			fileType: type,
			isDeleted: storyUpload.isDeleted,
			scanStatus: storyUpload.scanStatus,
			fileDetails: [
				{
					title: 'File name',
					value: fileName,
				},
				{
					title: 'File type',
					value: formatFileType(type),
				},
				{
					title: 'File size',
					value: formatFileSize(storyUpload.sizeInBytes),
				},
				{
					title: 'Submitted At',
					value: dayjs(storyUpload.createdAt).format('YYYY-MM-DD HH:mm:ss'),
				},
				{
					title: 'Story ID',
					value: storyUpload.storyId ?? '-',
				},
			],
		};
	}, [data]);

	const handleDownload = async () => {
		const { data: url } = await getPresignedUrl();

		if (!url) return;

		const a = document.createElement('a');
		a.href = url.url;
		a.download = '';

		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	const fileInfoItem = (title: string, value: string) => {
		return (
			<Flex vertical gap={10} className={css.fileInfoItem} key={title}>
				<Text
					tag="p"
					size={Text.size.description}
					color={Text.color.blackDark}
					weight="bold"
					transform="uppercase"
				>
					{title}
				</Text>
				<Text tag="p" weight="bold">
					{value}
				</Text>
			</Flex>
		);
	};

	if (!fileKey) {
		return <Redirect to={ROOT} />;
	}

	const { isDeleted, scanStatus } = fileData;
	const isUnavailableFile = isDeleted || scanStatus !== 'NoThreatsFound';

	return (
		!isFetching && (
			<LoginLayout className={css.layout} contentClassName={css.content}>
				<>
					<div className={css.asideContent}>
						<Button color="white" className={css.asideBtn}>
							{text.aside.tag}
						</Button>
						<Text tag="h5" size={Text.size.heading} color={Text.color.white}>
							{isUnavailableFile ? text.issues.asideTitle : text.aside.title}
						</Text>
						{!isUnavailableFile && (
							<Text tag="p" color={Text.color.white}>
								{text.aside.p}
							</Text>
						)}
						<Text tag="p" size={Text.size.body} color={Text.color.whiteDark}>
							{text.aside.note}
						</Text>
					</div>
					<div className={css.hero}>
						<PageTitle
							text={
								isDeleted
									? text.issues.deletedTitle
									: SCAN_STATUS_CONTENT[scanStatus]?.title ?? SCAN_STATUS_CONTENT.Unknown.title
							}
							className={css.pageTitle}
						/>
						{!isUnavailableFile && (
							<Text
								tag="p"
								size={Text.size.paragraph}
								color={Text.color.blackDark}
								className={css.heroSubtitle}
							>
								{text.hero.subtitle}
							</Text>
						)}
						<Card
							style={{ borderRadius: '20px', boxShadow: '0px 0 30px 0 var(--ra-color-black-soft)' }}
							title={
								isUnavailableFile ? (
									<Text tag="p" size={Text.size.subheading}>
										{isDeleted
											? text.issues.deletedCardTitle
											: SCAN_STATUS_CONTENT[scanStatus]?.cardTitle ??
												SCAN_STATUS_CONTENT.Unknown.cardTitle}
									</Text>
								) : (
									<Flex
										gap={20}
										align="center"
										justify="space-between"
										style={{ whiteSpace: 'normal' }}
									>
										<Flex gap={20} align="center">
											<div className={css.fileIcon}>{fileData.fileType}</div>
											<div>
												<Text tag="p" size={Text.size.subheading}>
													{fileData?.fileName}
												</Text>
												<Text
													tag="p"
													size={Text.size.description}
													color={Text.color.blackDark}
													weight="normal"
												>
													{text.hero.topCardNote}
												</Text>
											</div>
										</Flex>
										<Button
											type="button"
											view="secondary"
											size="medium"
											color="primary"
											onClick={handleDownload}
											disabled={isPending}
										>
											{text.hero.topCardBtn}
										</Button>
									</Flex>
								)
							}
						>
							<>
								{!isUnavailableFile && (
									<>
										<Grid columns="2" gap="medium">
											{fileData.fileDetails.map(i => {
												return fileInfoItem(i.title, i.value);
											})}
											<Flex gap={10} align="center" className={css.fileInfoItem}>
												<Player
													src={SecureValidation}
													background="transparent"
													controls={false}
													speed={1.25}
													autoplay
													keepLastFrame
													style={{ width: 60 }}
												/>
												<Flex vertical gap={10}>
													<Text
														tag="p"
														size={Text.size.description}
														color={Text.color.blackDark}
														weight="bold"
														transform="uppercase"
													>
														Security Checked
													</Text>
													<Text tag="p" weight="bold">
														Scanned for viruses and malware. No threats were detected.
													</Text>
												</Flex>
											</Flex>
										</Grid>
										<Divider />
									</>
								)}
								<Flex className={css.info} align="center">
									<Icon
										type="info"
										width={30}
										color={isUnavailableFile ? 'var(--ra-color-error)' : 'var(--ra-color-brand)'}
									/>
									<Text size={Text.size.paragraph} color={Text.color.blackDark}>
										{isDeleted
											? text.issues.deletedInfo
											: SCAN_STATUS_CONTENT[scanStatus]?.info ?? SCAN_STATUS_CONTENT.Unknown.info}
									</Text>
								</Flex>
								<Divider />
								<Flex gap={20} style={{ paddingBottom: '20px' }}>
									{!isUnavailableFile && (
										<Button onClick={handleDownload} disabled={isPending}>
											{text.hero.downloadBtn}
										</Button>
									)}
									<Link to={ROOT} className={css.link}>
										<Button view="secondary" color="primary">
											{text.hero.homeBtn}
										</Button>
									</Link>
								</Flex>
								<Text size={Text.size.footnote} color={Text.color.blackDark}>
									{text.hero.note}
								</Text>
							</>
						</Card>
					</div>
				</>
			</LoginLayout>
		)
	);
};

export default UploadedFile;
